import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import Toggle from "../../components/Toggle";
import Icon from "../../components/Icon";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { convertIntToMoney } from "../../helpers/conversions";

import "./styles.scss";

const statusLabels = {
    paid: 'Paga',
    pending: 'Aguardando',
    failed: 'Recusada',
    refunded: 'Estornada',
    canceled: 'Cancelada'
};

/**
 * Recarga automática (BDM-1059).
 *
 * A tela existe para o cliente confiar numa cobrança que acontece sem ele
 * apertar botão: por isso o teto em reais e a régua de recusa aparecem tão em
 * destaque quanto o gatilho.
 */
export const CreditAutoTopup = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        enabled: false,
        trigger: '',
        package_id: '',
        monthly_limit: 2,
        packages: [],
        card: null,
        monthly_cap_cents: null,
        used_this_month: 0,
        history: []
    });

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        setLoading(true);

        api.get('/credit/auto-topup')
            .then(res => setConfig({ ...res.data.resource, trigger: res.data.resource.trigger ?? '' }))
            .catch(renderError)
            .then(() => setLoading(false));
    }

    const save = () => {
        setSaving(true);

        api.put('/credit/auto-topup', {
            enabled: config.enabled,
            trigger: config.trigger === '' ? null : parseInt(config.trigger),
            package_id: config.package_id || null,
            monthly_limit: parseInt(config.monthly_limit) || 1
        }).then(res => {
            setConfig({ ...res.data.resource, trigger: res.data.resource.trigger ?? '' });
            toast.success('Recarga automática salva');
        }).catch(renderError).then(() => setSaving(false));
    }

    const selectedPackage = config.packages.find(item => item.id === config.package_id);
    const cap = selectedPackage ? selectedPackage.price_cents * (parseInt(config.monthly_limit) || 1) : null;

    return (
        <PanelTemplate id="credit-auto-topup">
            <PageHeader
                left={<h1>Recarga automática</h1>}
                right={<Button type={`primary`} size={`small`} svg={`save-white`} text={`Salvar`} action={save} loading={saving || loading} />}
            />

            <div className="auto-topup-columns">
                <Box
                    title={`Nunca ficar sem crédito no meio de um orçamento`}
                    tools={<Toggle
                        checked={config.enabled}
                        disabled={!config.card && !config.enabled}
                        label={`Ligar recarga automática`}
                        onChange={value => setConfig({ ...config, enabled: value })}
                    />}
                >
                    <div className="row">
                        <div className="col-6">
                            <Input
                                label={`Quando o saldo ficar abaixo de`}
                                value={config.trigger}
                                mask={`convertToInt`}
                                change={value => setConfig({ ...config, trigger: value })}
                            />
                        </div>
                        <div className="col-6">
                            <InputSelect
                                label={`Comprar o pacote de`}
                                value={config.package_id ?? ''}
                                options={config.packages.map(item => ({
                                    value: item.id,
                                    label: `${item.credits.toLocaleString('pt-BR')} créditos · ${convertIntToMoney(item.price_cents, true)}`
                                }))}
                                change={value => setConfig({ ...config, package_id: value })}
                            />
                        </div>
                    </div>

                    <div className="card-row">
                        <span className="card-label">
                            <Icon name={`card`} size={16} />
                            {config.card
                                ? `${config.card.brand ?? 'Cartão'} •••• ${config.card.last4}`
                                : 'Nenhum cartão salvo'}
                        </span>
                        {!config.card &&
                            <span className="card-hint">Para ligar a recarga, faça uma compra no cartão marcando "Salvar para a próxima compra".</span>
                        }
                    </div>
                    <p className="field-hint">Só cartão: Pix e boleto não podem ser cobrados sozinhos.</p>

                    <div className="row">
                        <div className="col-6">
                            <Input
                                label={`Máximo de recargas por mês`}
                                value={config.monthly_limit}
                                mask={`convertToInt`}
                                change={value => setConfig({ ...config, monthly_limit: value })}
                            />
                        </div>
                        <div className="col-6 cap-field">
                            <span className="cap-label">Teto de segurança</span>
                            <span className="cap-value">{cap ? convertIntToMoney(cap, true) : '—'}</span>
                            <span className="cap-note">
                                é o máximo que pode sair do cartão num mês · {config.used_this_month} de {parseInt(config.monthly_limit) || 1} {config.used_this_month === 1 ? 'usada' : 'usadas'} neste mês
                            </span>
                        </div>
                    </div>

                    <p className="field-hint">
                        Atingido o teto, avisamos por e-mail e paramos — nenhuma cobrança surpresa.
                        Cartão recusado é tentado de novo em 24 horas; depois de três recusas a recarga se desliga sozinha.
                    </p>
                </Box>

                <div>
                    <Box title={`Histórico de recargas`} className={`no-padding`}>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th className="right">Créditos</th>
                                        <th className="right">Valor</th>
                                        <th className="right">Situação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {config.history.length === 0 &&
                                        <tr><td colSpan={4} className="no-data">Nenhuma recarga automática até agora.</td></tr>
                                    }
                                    {config.history.map(item => (
                                        <tr key={item.id}>
                                            <td>{new Date(item.created_at).toLocaleDateString('pt-BR')}</td>
                                            <td className="right">{item.credits.toLocaleString('pt-BR')}</td>
                                            <td className="right">{convertIntToMoney(item.amount_cents, true)}</td>
                                            <td className="right">{statusLabels[item.status] ?? item.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Box>

                    {config.card?.failures_count > 0 &&
                        <div className="box-alert-warning">
                            Cartão recusado {config.card.failures_count}× seguida(s). Tentamos de novo em 24 horas e avisamos por e-mail;
                            após três recusas a recarga se desliga e o saldo volta a ser manual.
                        </div>
                    }
                </div>
            </div>
        </PanelTemplate>
    );
}

export default CreditAutoTopup;
